/**
 * Settings Drawer — right-sliding panel with grouped settings sections.
 * Sections: Subscription & Verification, App Settings, Help, About.
 * Sub-pages render only when active (lazy).
 */

import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Crown, Gift, Receipt, Users, BadgeCheck, CreditCard,
  Globe, Bell, MessageSquarePlus, ShieldBan,
  HelpCircle, Info, FileText, Shield, LogOut, ChevronRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@turnifly/shared/stores'
import { useProfile, useUpdateProfile } from '@turnifly/shared/hooks'
import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { useCategories } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import type { Category } from '@turnifly/shared/types'

import { PremiumPage } from './premium-page'
import { ReferralPage } from './referral-page'
import { VerificationPage } from './verification-page'
import { FeatureRequestPage } from './feature-request-page'
import { AboutPage } from './about-page'
import { LegalPage } from './legal-page'
import { PaymentHistoryPage } from './payment-history-page'
import { GiftPremiumPage } from './gift-premium-page'
import { CardsPage } from './cards-page'
import { BlockedUsersPage } from './blocked-users-page'

type SubPage =
  | 'premium' | 'referral' | 'verification' | 'featureRequest'
  | 'about' | 'terms' | 'privacy'
  | 'paymentHistory' | 'giftPremium' | 'paymentCards'
  | 'blockedUsers'
  | null

interface SettingsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = ({
  isOpen, onClose,
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { data: profile } = useProfile(user?.id)
  const { data: categories } = useCategories()
  const [subPage, setSubPage] = useState<SubPage>(null)
  const updateProfile = useUpdateProfile()

  const badge = getBadgeDisplay(profile?.is_premium ?? false, profile?.badge_type)

  const toggleNotifications = useCallback((): void => {
    if (!profile) return
    const nextVal = !profile.notifications_enabled
    updateProfile.mutate({ userId: profile.id, updates: { notifications_enabled: nextVal } })
    hapticImpact('light')
  }, [profile, updateProfile])

  const handleLogout = useCallback((): void => {
    hapticImpact('medium')
    localStorage.removeItem('turnifly_access_token')
    localStorage.removeItem('turnifly_refresh_token')
    logout()
    onClose()
  }, [logout, onClose])

  const toggleLanguage = useCallback((): void => {
    const nextLang = i18n.language === 'uz' ? 'en' : 'uz'
    void i18n.changeLanguage(nextLang)
    localStorage.setItem('turnifly_lang', nextLang)
    hapticImpact('light')
  }, [i18n])

  const handleSupport = useCallback((): void => {
    onClose()
    navigate('/inbox')
  }, [onClose, navigate])

  const closeSubPage = useCallback((): void => setSubPage(null), [])

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed top-0 right-0 bottom-0 w-[85vw] max-w-xs lg:max-w-sm z-50
                bg-bg border-l border-white/[0.06] flex flex-col"
            >
              {/* Header — with fullscreen safe area */}
              <div className="px-5 pb-5 border-b border-white/[0.08]" style={{ paddingTop: 'max(32px, calc(var(--tg-safe-area-inset-top) + var(--tg-content-safe-area-inset-top) + 12px))' }}>
                <div className="flex items-center gap-3">
                  <AvatarWithBadge
                    src={profile?.avatar_url ?? user?.avatar_url}
                    fallback={(profile?.full_name ?? user?.full_name ?? 'U').charAt(0)}
                    size="lg"
                    badge={badge}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-text truncate">
                      {profile?.full_name ?? user?.full_name}
                    </h3>
                    {profile?.username && (
                      <p className="text-sm text-text-hint mt-0.5">@{profile.username}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Sections */}
              <div className="flex-1 overflow-y-auto">
                {/* ── Personal Info ─────────────────────── */}
                <PersonalInfoBlock profile={profile} categories={categories} />

                {/* ── Subscription & Verification ───────── */}
                <SectionTitle label={t('profile.sections.subscription')} />
                <MenuItem icon={Crown} label={t('profile.settings.premium')}
                  badge={profile?.is_premium ? t('common.active') : undefined}
                  onClick={() => setSubPage('premium')} />
                <MenuItem icon={Gift} label={t('profile.settings.giftPremium')}
                  onClick={() => setSubPage('giftPremium')} />
                <MenuItem icon={CreditCard} label={t('profile.settings.paymentCards')}
                  onClick={() => setSubPage('paymentCards')} />
                <MenuItem icon={Receipt} label={t('profile.settings.paymentHistory')}
                  onClick={() => setSubPage('paymentHistory')} />
                <MenuItem icon={Users} label={t('profile.settings.referral')}
                  onClick={() => setSubPage('referral')} />
                <MenuItem icon={BadgeCheck} label={t('profile.settings.verification')}
                  onClick={() => setSubPage('verification')} />

                <SectionDivider />

                {/* ── App Settings ──────────────────────── */}
                <SectionTitle label={t('profile.sections.appSettings')} />
                <MenuItem icon={Globe} label={t('profile.settings.language')}
                  rightLabel={i18n.language === 'uz' ? "O'zbek" : 'English'}
                  onClick={toggleLanguage} />
                <MenuItem icon={Bell} label={t('profile.settings.notifications')}
                  rightLabel={profile?.notifications_enabled ? t('common.on') : t('common.off')}
                  onClick={toggleNotifications} />
                <MenuItem icon={ShieldBan} label={t('profile.settings.blockedUsers')}
                  onClick={() => setSubPage('blockedUsers')} />

                <SectionDivider />

                {/* ── Help & Feedback ──────────────────── */}
                <SectionTitle label={t('profile.sections.help')} />
                <MenuItem icon={MessageSquarePlus} label={t('profile.settings.featureRequest')}
                  onClick={() => setSubPage('featureRequest')} />
                <MenuItem icon={HelpCircle} label={t('profile.settings.support')}
                  onClick={handleSupport} />

                <SectionDivider />

                {/* ── About ────────────────────────────── */}
                <SectionTitle label={t('profile.sections.about')} />
                <MenuItem icon={Info} label={t('profile.settings.about')}
                  onClick={() => setSubPage('about')} />
                <MenuItem icon={FileText} label={t('profile.settings.terms')}
                  onClick={() => setSubPage('terms')} />
                <MenuItem icon={Shield} label={t('profile.settings.privacy')}
                  onClick={() => setSubPage('privacy')} />

                <div className="h-4" />
              </div>

              {/* Logout */}
              <div className="p-4 border-t border-white/[0.08]">
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl
                    text-danger hover:bg-danger/10 transition-colors tap-highlight-transparent"
                >
                  <LogOut size={18} />
                  <span className="font-semibold">{t('profile.settings.logout')}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Sub-pages (lazy) */}
      {subPage === 'premium' && <PremiumPage isOpen onClose={closeSubPage} />}
      {subPage === 'referral' && <ReferralPage isOpen onClose={closeSubPage} />}
      {subPage === 'verification' && <VerificationPage isOpen onClose={closeSubPage} />}
      {subPage === 'featureRequest' && <FeatureRequestPage isOpen onClose={closeSubPage} />}
      {subPage === 'about' && <AboutPage isOpen onClose={closeSubPage} />}
      {subPage === 'terms' && <LegalPage isOpen onClose={closeSubPage} mode="terms" />}
      {subPage === 'privacy' && <LegalPage isOpen onClose={closeSubPage} mode="privacy" />}
      {subPage === 'paymentHistory' && <PaymentHistoryPage isOpen onClose={closeSubPage} />}
      {subPage === 'giftPremium' && <GiftPremiumPage isOpen onClose={closeSubPage} />}
      {subPage === 'paymentCards' && <CardsPage isOpen onClose={closeSubPage} />}
      {subPage === 'blockedUsers' && <BlockedUsersPage isOpen onClose={closeSubPage} userId={user?.id ?? ''} />}
    </>
  )
}

// ── Personal Info Block ──────────────────────────────────────

import type { Profile } from '@turnifly/shared/types'

interface PersonalInfoBlockProps {
  profile: Profile | undefined
  categories: unknown[] | undefined
}

const PersonalInfoBlock: React.FC<PersonalInfoBlockProps> = ({ profile, categories }) => {
  const { t, i18n } = useTranslation()

  if (!profile) return null

  const hasProfession = !!profile.profession
  const hasInterests = profile.interests.length > 0

  if (!hasProfession && !hasInterests) return null

  const catMap = new Map(
    ((categories ?? []) as Category[]).map((c) => [c.id, c]),
  )

  const resolvedInterests = profile.interests
    .map((id) => catMap.get(id))
    .filter((c): c is Category => c !== undefined)

  return (
    <>
      <SectionTitle label={t('profile.sections.personal')} />
      <div className="px-5 pb-3 space-y-2">
        {hasProfession && (
          <div className="flex items-center gap-2 text-sm text-text/70">
            <span className="text-text-hint">💼</span>
            <span>{profile.profession}</span>
          </div>
        )}
        {resolvedInterests.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {resolvedInterests.map((cat) => (
              <span
                key={cat.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                  text-[11px] bg-white/[0.04] border border-white/[0.06] text-text-hint"
              >
                <span>{cat.icon}</span>
                <span>{i18n.language === 'uz' ? cat.name_uz : cat.name_en}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <SectionDivider />
    </>
  )
}

// ── Section components ──────────────────────────────────────

const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
  <div className="px-5 pt-4 pb-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-text-hint/60">
      {label}
    </span>
  </div>
)

const SectionDivider: React.FC = () => (
  <div className="mx-5 my-2 h-px bg-white/[0.06]" />
)

interface MenuItemProps {
  icon: React.ElementType
  label: string
  badge?: string
  rightLabel?: string
  onClick?: () => void
}

const MenuItem: React.FC<MenuItemProps> = React.memo(({
  icon: Icon, label, badge, rightLabel, onClick,
}) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className="flex items-center gap-3 w-full px-5 py-3 hover:bg-white/[0.03]
      active:bg-white/[0.05] transition-colors tap-highlight-transparent
      disabled:opacity-40 disabled:cursor-default"
  >
    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] shrink-0">
      <Icon size={16} className="text-text-hint" />
    </div>
    <span className="flex-1 text-sm text-text text-left">{label}</span>
    {badge && <Badge variant="success" className="text-[10px]">{badge}</Badge>}
    {rightLabel && <span className="text-xs text-text-hint">{rightLabel}</span>}
    {onClick && <ChevronRight size={14} className="text-text-hint/40 shrink-0" />}
  </button>
))
MenuItem.displayName = 'MenuItem'
